/**
 * CustomerAppShell — de echte klant-app voor `/app` (Iter 78).
 *
 * Werkt zonder app-download. Mobiel = bottom-nav. Desktop = sidebar.
 * Tabs: Overzicht · Apparaten · P1 · Zonnestroom · Verbruik · Advies · Offertes · Account · Hulp.
 * Data per klant via `/api/customer/app/*`.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, Sun, Plug, Activity, BarChart3, Lightbulb, FileText, User,
  HelpCircle, ArrowRight, CheckCircle2, AlertCircle, LogOut, Plus, Loader2,
  Camera, QrCode, Search, Sparkles, X, Pencil, Trash2, MapPin,
} from 'lucide-react';
import { customerApi, customerLogout } from '@/hooks/useCustomerAuth';
import AppSidebar from '@/components/customer-app/AppSidebar';
import AppBottomNav from '@/components/customer-app/AppBottomNav';
import InstallPrompt from '@/components/customer-app/InstallPrompt';
import AppOnboardingSetup, { isSetupCompleted as isLocalSetupCompleted } from '@/components/customer-app/AppOnboardingSetup';
import MyDeviceDataPanel from '@/components/customer-app/MyDeviceDataPanel';
import PilotOptInCard from '@/components/customer-app/PilotOptInCard';

/* ----------------- Shared atoms ----------------- */
function ScreenTitle({ title, sub, badge }) {
  return (
    <div className="mb-4">
      {badge && <div className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold mb-1">{badge}</div>}
      <h2 className="text-xl sm:text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{title}</h2>
      {sub && <p className="text-sm text-white/65 mt-1.5 max-w-prose">{sub}</p>}
    </div>
  );
}
function Card({ children, className = '', testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-white/10 p-4 sm:p-5 ${className}`}
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
    >{children}</div>
  );
}
function StatusBadge({ tone = 'slate', children }) {
  const cls = {
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    sky: 'bg-sky-500/15 text-sky-300 border-sky-400/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    slate: 'bg-white/5 text-white/65 border-white/15',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
  }[tone];
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-medium ${cls}`}>{children}</span>;
}

/* ----------------- Image helpers (Iter 80 P2) ----------------- */
// Iter 80 — chunked + downscaled image → base64. Replaces the previous
// `btoa(String.fromCharCode(...new Uint8Array(buf)))` which blew the call
// stack on >100 KB images and made Android photo upload always fail.
const MAX_IMAGE_DIM = 1600;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;

function arrayBufferToBase64Chunked(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  const chunk = 0x8000; // 32 KB — safe under stack limits on all browsers.
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

async function readFileAsImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ img, url }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image_load_failed')); };
    img.src = url;
  });
}

async function fileToCompressedBase64(file) {
  // Try to downscale via canvas — keeps payload small AND avoids the
  // 8 MB backend cap. If decoding fails (rare), fall back to chunked b64
  // of the raw file as-is.
  try {
    const { img, url } = await readFileAsImage(file);
    try {
      const ratio = Math.min(1, MAX_IMAGE_DIM / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * ratio));
      const h = Math.max(1, Math.round(img.naturalHeight * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      // dataUrl = data:image/jpeg;base64,XXXX — strip prefix
      const b64 = dataUrl.split(',', 2)[1] || '';
      return { base64: b64, mime: 'image/jpeg' };
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    if (file.size > MAX_IMAGE_BYTES) {
      throw new Error('Foto is te groot. Maak een nieuwe foto of kies een kleinere.');
    }
    const buf = await file.arrayBuffer();
    return { base64: arrayBufferToBase64Chunked(buf), mime: file.type || 'image/jpeg' };
  }
}

/* ----------------- AddDevice modal ----------------- */
const CATEGORIES = [
  { id: 'p1',         label: 'P1-meter' },
  { id: 'omvormer',   label: 'Omvormer' },
  { id: 'laadpaal',   label: 'Laadpaal' },
  { id: 'warmtepomp', label: 'Warmtepomp' },
  { id: 'boiler',     label: 'Boiler' },
  { id: 'batterij',   label: 'Thuisbatterij' },
  { id: 'stekker',    label: 'Slimme stekker' },
  { id: 'airco',      label: 'Airco' },
  { id: 'panelen',    label: 'Zonnepanelen' },
];

function AddDeviceFlow({ open, onClose, onAdded }) {
  const [step, setStep] = useState('choose');  // choose | photo | qr | discover | manual | confirm
  const [busy, setBusy] = useState(false);
  const [recognized, setRecognized] = useState(null);
  const [manual, setManual] = useState({ category: '', brand: '', model: '', custom_name: '', measurable: 'onbekend', control_wanted: false });
  const [err, setErr] = useState('');

  useEffect(() => { if (!open) { setStep('choose'); setRecognized(null); setManual({ category: '', brand: '', model: '', custom_name: '', measurable: 'onbekend', control_wanted: false }); setErr(''); } }, [open]);
  if (!open) return null;

  const handlePhotoFile = async (file) => {
    if (!file) return;
    setErr(''); setBusy(true);
    try {
      const { base64, mime } = await fileToCompressedBase64(file);
      const res = await customerApi.post('/customer/app/devices/recognize-photo', {
        image_base64: base64, mime_type: mime, keep_photo: false,
      });
      if (res.data?.ok && res.data?.extracted) {
        const ex = res.data.extracted;
        setRecognized(ex);
        setManual((m) => ({
          ...m,
          category: ex.category || m.category,
          brand: ex.brand || m.brand,
          model: ex.model || m.model,
        }));
        setStep('confirm');
      } else {
        // Vision/OCR returned no extraction — friendly fallback, NOT a hard
        // error. Customer can still save the device by filling in fields.
        setErr('We konden het label niet automatisch herkennen. Vul de gegevens handmatig aan.');
        setStep('manual');
      }
    } catch (e) {
      // Network / upload / size error. Never block the customer here —
      // route to manual entry with a friendly explanation.
      const detail = e?.response?.data?.detail || e?.message || '';
      const friendly = detail && detail.length < 160
        ? `Foto kon niet worden verwerkt: ${detail}. Vul de gegevens handmatig aan.`
        : 'We konden de foto niet verwerken. Vul de gegevens handmatig aan.';
      setErr(friendly);
      setStep('manual');
    } finally { setBusy(false); }
  };

  const submit = async (source) => {
    setErr(''); setBusy(true);
    try {
      if (!manual.category) { setErr('Kies eerst een categorie.'); setBusy(false); return; }
      const payload = { ...manual, source: source || 'manual', recognized };
      const res = await customerApi.post('/customer/app/devices', payload);
      if (res.data?.ok) { onAdded?.(res.data.device); onClose?.(); }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Opslaan mislukt.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm" role="dialog" data-testid="add-device-overlay">
      <div className="w-full max-w-lg rounded-2xl border border-emerald-400/30 shadow-2xl p-5 sm:p-6 relative" style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }} data-testid="add-device-sheet">
        <button onClick={onClose} aria-label="Sluiten" className="absolute top-3 right-3 text-white/55 hover:text-emerald-300" data-testid="add-device-close"><X className="w-5 h-5" /></button>

        {step === 'choose' && (
          <div data-testid="add-device-choose">
            <ScreenTitle badge="Apparaat toevoegen" title="Hoe wilt u uw apparaat toevoegen?" sub="Kies de manier die het beste past." />
            <div className="grid grid-cols-1 gap-2">
              <ChoiceTile Icon={Camera} title="Foto maken van label" detail="Wij vullen merk/model alvast in" onClick={() => setStep('photo')} testId="add-device-choose-photo" />
              <ChoiceTile Icon={QrCode} title="QR-code scannen" detail="Plak QR-tekst — fallback handmatig" onClick={() => setStep('qr')} testId="add-device-choose-qr" />
              <ChoiceTile Icon={Search} title="Zoeken in de buurt" detail="Voor ondersteunde apparaten" onClick={() => setStep('discover')} testId="add-device-choose-discover" />
              <ChoiceTile Icon={Pencil} title="Handmatig toevoegen" detail="Vul categorie, merk en model in" onClick={() => setStep('manual')} testId="add-device-choose-manual" />
            </div>
          </div>
        )}

        {step === 'photo' && (
          <div data-testid="add-device-photo">
            <ScreenTitle badge="Foto van label" title="Maak of upload een foto" sub="Wij herkennen merk en model en u bevestigt of past aan." />
            <label className="block rounded-md border border-white/15 bg-white/5 px-3 py-6 text-center text-sm text-white/75 hover:border-emerald-400/40 cursor-pointer">
              <input
                type="file" accept="image/*" capture="environment"
                onChange={(e) => handlePhotoFile(e.target.files?.[0])}
                className="hidden"
                data-testid="add-device-photo-input"
              />
              <Camera className="w-6 h-6 mx-auto text-emerald-300 mb-2" />
              {busy ? 'Bezig met herkennen…' : 'Tik om foto te kiezen of camera te openen'}
            </label>
            {err && <p className="text-xs text-rose-300 mt-2">{err}</p>}
            <button onClick={() => setStep('manual')} className="text-xs text-white/55 mt-3 underline">Overslaan → handmatig</button>
          </div>
        )}

        {step === 'qr' && (
          <div data-testid="add-device-qr">
            <ScreenTitle badge="QR-code" title="Plak QR-tekst" sub="Camera-QR-detectie volgt later. Voor nu kunt u de tekst plakken." />
            <textarea
              rows={3} className="w-full text-sm bg-white/5 border border-white/15 rounded-md p-2 text-white/85"
              placeholder="Plak hier de QR-inhoud"
              onChange={(e) => {
                const t = (e.target.value || '').trim();
                if (t) setManual((m) => ({ ...m, model: m.model || t.slice(0, 40) }));
              }}
              data-testid="add-device-qr-input"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setStep('manual')} className="px-3 py-2 rounded-md border border-white/15 text-sm text-white/85" data-testid="add-device-qr-continue">Volgende → handmatig bevestigen</button>
            </div>
          </div>
        )}

        {step === 'discover' && (
          <div data-testid="add-device-discover">
            <ScreenTitle badge="Zoeken in de buurt" title="Niet beschikbaar in deze versie" sub="Veiligheidsredenen: lokaal scannen volgt in een latere versie. Kies een veilige route." />
            <p className="text-[12px] text-white/65 mb-3 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-amber-300" /> Lokale netwerk-discovery is read-only en alleen voor geverifieerde driver-bibliotheken — daarom uit voor nu.</p>
            <div className="flex flex-col gap-2">
              <button onClick={() => setStep('photo')} className="px-3 py-2 rounded-md border border-white/15 text-sm text-white/85">Foto van label maken</button>
              <button onClick={() => setStep('manual')} className="px-3 py-2 rounded-md border border-white/15 text-sm text-white/85">Handmatig toevoegen</button>
            </div>
          </div>
        )}

        {(step === 'manual' || step === 'confirm') && (
          <div data-testid={step === 'confirm' ? 'add-device-confirm' : 'add-device-manual'}>
            <ScreenTitle
              badge={step === 'confirm' ? 'Bevestig herkenning' : 'Handmatig invoeren'}
              title={step === 'confirm' ? 'Klopt dit apparaat?' : 'Vul de gegevens in'}
              sub="U kunt naam en sturingvoorkeur later wijzigen."
            />
            <div className="space-y-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-white/55">Categorie</label>
                <select value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white/90" data-testid="add-device-category">
                  <option value="">— kies —</option>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={manual.brand || ''} onChange={(e) => setManual({ ...manual, brand: e.target.value })} placeholder="Merk" className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white/90" data-testid="add-device-brand" />
                <input value={manual.model || ''} onChange={(e) => setManual({ ...manual, model: e.target.value })} placeholder="Model" className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white/90" data-testid="add-device-model" />
              </div>
              <input value={manual.custom_name || ''} onChange={(e) => setManual({ ...manual, custom_name: e.target.value })} placeholder="Eigen naam (optioneel)" className="w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white/90" data-testid="add-device-name" />
              <div className="grid grid-cols-2 gap-2">
                <select value={manual.measurable} onChange={(e) => setManual({ ...manual, measurable: e.target.value })} className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white/90" data-testid="add-device-measurable">
                  <option value="onbekend">Meetbaar: onbekend</option>
                  <option value="ja">Meetbaar: ja</option>
                  <option value="nee">Meetbaar: nee</option>
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-white/75 bg-white/5 border border-white/15 rounded-md px-3 py-2">
                  <input type="checkbox" checked={!!manual.control_wanted} onChange={(e) => setManual({ ...manual, control_wanted: e.target.checked })} data-testid="add-device-control" />
                  Sturing gewenst (later)
                </label>
              </div>
            </div>
            {err && <p className="text-xs text-rose-300 mt-2">{err}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="px-3 py-2 rounded-md border border-white/15 text-sm text-white/85" data-testid="add-device-cancel">Annuleren</button>
              <button onClick={() => submit(step === 'confirm' ? 'photo' : 'manual')} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-500 text-emerald-950 font-semibold text-sm disabled:opacity-60" data-testid="add-device-submit">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Apparaat opslaan
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChoiceTile({ Icon, title, detail, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="w-full text-left flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-3 hover:bg-white/10 hover:border-emerald-400/30 transition-colors">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{title}</div>
        <div className="text-[12px] text-white/55 leading-snug">{detail}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-emerald-300" />
    </button>
  );
}

/* ----------------- Screens ----------------- */
function OverviewScreen({ profile, devices, user, onAddDevice, onP1 }) {
  const setupDone = profile?.setup_completed;
  const p1 = (profile?.p1) || {};
  const meetbron = p1.status === 'gekoppeld' ? { tone: 'emerald', text: 'Gekoppeld' } : p1.status === 'voorbereid' ? { tone: 'sky', text: 'Voorbereid' } : { tone: 'amber', text: 'Niet gekoppeld' };
  // Iter 80 P0 — strict real-customer greeting. NEVER use "Owner Demo" or
  // any pilot label. Show the customer's name or, if missing, fall back to
  // a neutral welkomstitel — never a demo persona name.
  const accountName = (user?.name || profile?.user?.name || '').trim();
  const accountEmail = (user?.email || profile?.user?.email || '').trim();
  const greetingTitle = accountName
    ? `Hoi ${accountName}`
    : 'Welkom in Mijn Powersure';
  return (
    <div className="space-y-3" data-testid="screen-overview">
      <ScreenTitle
        badge="Energy Control actief"
        title={greetingTitle}
        sub={accountEmail
          ? `Uw persoonlijke Powersure-overzicht voor ${accountEmail}. Geen sturing zonder uw goedkeuring.`
          : 'Uw persoonlijke Powersure-overzicht. Geen sturing zonder uw goedkeuring.'}
      />
      <InstallPrompt />
      <Card testId="overview-status-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-white/55 font-medium">Status</div>
            <div className="text-sm font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>Energy Control actief</div>
          </div>
          <StatusBadge tone="emerald"><CheckCircle2 className="w-2.5 h-2.5" /> Actief</StatusBadge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-md bg-white/5 border border-white/10 p-2">
            <div className="text-[10px] uppercase tracking-wider text-white/55">Meetbron / P1</div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-white">{p1.kind || 'Nog niet gekozen'}</span>
              <StatusBadge tone={meetbron.tone}>{meetbron.text}</StatusBadge>
            </div>
          </div>
          <div className="rounded-md bg-white/5 border border-white/10 p-2">
            <div className="text-[10px] uppercase tracking-wider text-white/55">Apparaten</div>
            <div className="text-sm text-white mt-1">{devices.length} gekoppeld</div>
          </div>
        </div>
      </Card>

      <Card testId="overview-top3-card">
        <div className="text-[10.5px] uppercase tracking-wider text-emerald-300/90 font-semibold mb-2">Top 3 kansen</div>
        <ul className="space-y-1.5">
          <li className="flex items-center justify-between"><span className="text-sm text-white/90">Laad uw EV met zonnestroom</span><StatusBadge tone="emerald">Aanbevolen</StatusBadge></li>
          <li className="flex items-center justify-between"><span className="text-sm text-white/90">Omvormer-richting bijstellen</span><StatusBadge tone="sky">Eerst meten</StatusBadge></li>
          <li className="flex items-center justify-between"><span className="text-sm text-white/90">Boilertemperatuur verlagen</span><StatusBadge tone="amber">Later plannen</StatusBadge></li>
        </ul>
      </Card>

      <Card testId="overview-next-step-card" className="!p-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 shrink-0">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Wat kunt u nu doen?</div>
            <p className="text-[12.5px] text-white/65 mt-1 leading-snug">
              {meetbron.tone === 'emerald'
                ? 'Voeg een apparaat toe om uw plan persoonlijker te maken.'
                : 'Koppel uw P1-meter zodat we live inzicht kunnen tonen — daarna apparaten.'}
            </p>
            {meetbron.tone === 'emerald' ? (
              <button onClick={onAddDevice} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 text-emerald-950 font-semibold text-sm" data-testid="overview-cta-add-device">
                <Plus className="w-3.5 h-3.5" /> Apparaat toevoegen
              </button>
            ) : (
              <button onClick={onP1} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 text-emerald-950 font-semibold text-sm" data-testid="overview-cta-p1">
                <Activity className="w-3.5 h-3.5" /> P1-meter koppelen
              </button>
            )}
            {!setupDone && (
              <p className="text-[11px] text-amber-300/90 mt-2"><AlertCircle className="w-3 h-3 inline mr-1" /> Onboarding niet voltooid — vul de 4 vragen in voor beter advies.</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function DevicesScreen({ devices, onAdd, onDelete }) {
  const grouped = {};
  CATEGORIES.forEach((c) => { grouped[c.id] = []; });
  devices.forEach((d) => { (grouped[d.category] || (grouped[d.category] = [])).push(d); });
  return (
    <div className="space-y-3" data-testid="screen-devices">
      <ScreenTitle badge="Apparaten" title="Uw verbonden apparaten" sub="Tegels per soort. Voeg toe, bekijk of verwijder per apparaat." />
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-white/65">{devices.length} apparaat{devices.length === 1 ? '' : 'en'} toegevoegd</div>
          <button onClick={onAdd} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500 text-emerald-950 font-semibold text-sm" data-testid="devices-add-btn">
            <Plus className="w-3.5 h-3.5" /> Apparaat toevoegen
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CATEGORIES.map((c) => {
            const list = grouped[c.id] || [];
            const tone = list.length > 0 ? 'emerald' : 'slate';
            const status = list.length > 0 ? 'Gekoppeld' : 'Nog niet gekoppeld';
            return (
              <div key={c.id} className="rounded-md bg-white/5 border border-white/10 p-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{c.label}</span>
                  <StatusBadge tone={tone}>{status}</StatusBadge>
                </div>
                {list.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {list.map((d) => (
                      <li key={d.id} className="flex items-center justify-between text-[12px] text-white/75 bg-white/3 rounded px-2 py-1" data-testid={`device-row-${d.id}`}>
                        <span className="truncate">{d.custom_name || d.brand || d.model || c.label}</span>
                        <button onClick={() => onDelete(d.id)} className="text-rose-300/80 hover:text-rose-200" data-testid={`device-delete-${d.id}`} aria-label="Verwijder">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function P1Screen({ p1, onSet }) {
  const status = p1?.status || 'niet_gekoppeld';
  return (
    <div className="space-y-3" data-testid="screen-p1">
      <ScreenTitle badge="P1 / slimme meter" title="Koppel uw meetbron" sub="Live inzicht begint met meten. Kies de route die bij u past." />
      <Card>
        <p className="text-sm text-white/75 leading-relaxed">Voor actueel inzicht in uw verbruik en zonnestroom heeft u een meetbron nodig — meestal een <strong className="text-white">P1-meter of slimme meter</strong>. Zonder koppeling kunt u alvast voorbeelden zien. Met koppeling worden adviezen persoonlijker.</p>
        <p className="text-[12px] text-emerald-300/90 mt-2 leading-snug">Eerst meten en begrijpen. Daarna advies. Sturing alleen waar veilig en met uw goedkeuring.</p>
      </Card>
      <div className="grid grid-cols-1 gap-2">
        {[
          { id: 'homewizard', t: 'HomeWizard P1', d: 'Stekkerbare P1-poort, eenvoudige set-up.' },
          { id: 'youless',    t: 'Youless LS120',  d: 'Optisch lezen, plug-and-play.' },
          { id: 'manual',     t: 'Handmatig invoeren', d: 'Vul maandelijkse meterstanden zelf in.' },
          { id: 'later',      t: 'Later koppelen', d: 'Ga eerst zonder live inzicht door.' },
        ].map((o) => (
          <button
            key={o.id}
            onClick={() => onSet(o.id)}
            className="w-full text-left flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-3 hover:bg-white/10 hover:border-emerald-400/30"
            data-testid={`p1-option-${o.id}`}
          >
            <div>
              <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{o.t}</div>
              <div className="text-[12px] text-white/55 leading-snug">{o.d}</div>
            </div>
            {p1?.kind === o.id && <StatusBadge tone="emerald">Gekozen</StatusBadge>}
          </button>
        ))}
      </div>
      <Card>
        <div className="text-[10.5px] uppercase tracking-wider text-white/55 font-medium">Huidige status</div>
        <div className="text-sm text-white mt-1">{status === 'gekoppeld' ? 'Gekoppeld' : status === 'voorbereid' ? 'Voorbereid' : status === 'later' ? 'Later koppelen' : 'Niet gekoppeld'}</div>
      </Card>
    </div>
  );
}

function SolarScreen() {
  return (
    <div className="space-y-3" data-testid="screen-solar">
      <ScreenTitle badge="Zonnestroom" title="Eigen zonnestroom slimmer benutten" sub="Live inzicht in opwek, verbruik en teruglevering — zodra uw P1 gekoppeld is." />
      <Card>
        <ul className="text-sm text-white/85 space-y-1.5">
          <li className="flex items-center gap-2"><Sun className="w-3.5 h-3.5 text-amber-300" /> Eigen verbruik verhogen door planning</li>
          <li className="flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-emerald-300" /> Teruglevering rustig spreiden</li>
          <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-sky-300" /> Salderen 2027 — wij volgen de wet, geen paniek</li>
        </ul>
        <p className="text-[12px] text-white/55 mt-3 leading-snug">Geen euroclaims zonder echte berekening op basis van uw meting.</p>
      </Card>
    </div>
  );
}

function UsageScreen() {
  return (
    <div className="space-y-3" data-testid="screen-usage">
      <ScreenTitle badge="Verbruik" title="Waar gaat uw stroom heen?" sub="Identificeer pieken en verschuif gebruik naar zonuren." />
      <Card>
        <ul className="text-sm text-white/85 space-y-1.5">
          <li>• Wasdroger en oven zijn vaak de grootste pieken.</li>
          <li>• Boiler en warmtepomp lopen vaak het langst.</li>
          <li>• Slimme stekkers helpen kleine verbruikers timen.</li>
        </ul>
        <p className="text-[12px] text-white/55 mt-3 leading-snug">Detail-inzicht per apparaat volgt zodra uw P1 gekoppeld is.</p>
      </Card>
    </div>
  );
}

function AdviceScreen({ advice, onAddDevice }) {
  return (
    <div className="space-y-3" data-testid="screen-advice">
      <ScreenTitle badge="Advies" title="Volgende stappen voor uw woning" sub="Korte, concrete adviezen — u kiest of u doorgaat." />
      <div className="grid grid-cols-1 gap-2">
        {(advice || []).map((a) => (
          <Card key={a.id} testId={`advice-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{a.title}</div>
                <p className="text-[12px] text-white/65 leading-snug mt-1">{a.detail}</p>
              </div>
              <StatusBadge tone={a.tone || 'slate'}>{a.badge}</StatusBadge>
            </div>
          </Card>
        ))}
      </div>
      <button onClick={onAddDevice} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 font-medium text-sm" data-testid="advice-cta-add">
        Voeg apparaat toe voor persoonlijker advies <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function QuotesScreen({ quotes }) {
  return (
    <div className="space-y-3" data-testid="screen-quotes">
      <ScreenTitle badge="Offertes" title="Uw offerteaanvragen en voorstellen" sub="U kunt offertes bekijken, goedkeuren, afwijzen of een vraag stellen." />
      {(!quotes || quotes.length === 0) && (
        <Card>
          <p className="text-sm text-white/65">Nog geen offertes. Vraag een offerte aan vanuit Advies of via uw installateur-partner.</p>
        </Card>
      )}
      {(quotes || []).map((q) => (
        <Card key={q.id} testId={`quote-row-${q.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{q.subject}</div>
              <div className="text-[12px] text-white/55">{q.created_at}</div>
            </div>
            <StatusBadge tone="sky">{q.status}</StatusBadge>
          </div>
        </Card>
      ))}
    </div>
  );
}

function AccountScreen({ account, onLogout }) {
  const sub = account?.subscription || {};
  const portal = account?.billing_portal || {};
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingErr, setBillingErr] = useState('');

  // Fase 3.4 — derive a stable session id for the device-data panel without
  // leaking the customer access token. Hash of email is enough scope-wise.
  const customerEmail = (account?.email || '').trim().toLowerCase();
  const deviceDataSid = customerEmail
    ? 'cust-' + Array.from(customerEmail).reduce((a, c) => ((a * 31 + c.charCodeAt(0)) | 0), 0)
        .toString(16).replace('-', 'n').padStart(8, '0')
    : undefined;

  // Blok 81 — friendly post-portal return banner. The Stripe Billing Portal
  // returns to /app?billing=returned. We surface a soft confirmation so the
  // klant weet dat de webhook nog kan landen (geen valse "active"-status).
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const justReturned = params ? params.get('billing') === 'returned' : false;
  useEffect(() => {
    if (!justReturned) return;
    // Strip the query string once we've shown the banner so reloads stay clean.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('billing');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
  }, [justReturned]);

  const statusLabel = (() => {
    switch (sub.status) {
      case 'active': return 'Actief';
      case 'past_due': return 'Actie nodig — betaling mislukt';
      case 'cancelled': return 'Opgezegd';
      case 'pending': return 'In afwachting van Stripe-bevestiging';
      case 'none':
      case undefined: return 'Nog niet geactiveerd';
      default: return sub.status;
    }
  })();

  const openPortal = async () => {
    setBillingErr('');
    setBillingBusy(true);
    try {
      const r = await customerApi.post('/customer/billing/portal-session');
      const url = r.data?.url;
      if (!url) throw new Error('Geen portal-URL ontvangen.');
      // Top-level redirect — Stripe must own the URL fragment / cookies.
      window.location.assign(url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === 'object' ? d?.error : null;
      const msg = typeof d === 'object' ? d?.message : (typeof d === 'string' ? d : '');
      if (code === 'portal_not_configured') {
        setBillingErr('Het klantportaal wordt nog geactiveerd. Probeer het later opnieuw.');
      } else if (code === 'no_subscription') {
        setBillingErr('We konden nog geen actief abonnement vinden.');
      } else if (code === 'no_stripe_customer') {
        setBillingErr('We missen nog uw betaalgegevens. Probeer opnieuw zodra Stripe uw eerste betaling heeft bevestigd.');
      } else {
        setBillingErr(msg || 'Beheer is tijdelijk niet beschikbaar. Probeer het later opnieuw.');
      }
      setBillingBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="screen-account">
      <ScreenTitle badge="Account" title={account?.email || 'Mijn Powersure'} sub="Abonnement, betaling en uitloggen." />

      {justReturned && (
        <div className="rounded-md border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-[12px] text-emerald-100" data-testid="billing-returned-banner">
          U bent terug vanuit Stripe. Uw betalingsgegevens worden automatisch bijgewerkt zodra Stripe dit verwerkt.
        </div>
      )}

      <Card testId="account-subscription-card">
        <div className="text-[10.5px] uppercase tracking-wider text-white/55 font-medium">Abonnement & betaling</div>
        <div className="mt-1 text-sm text-white" data-testid="account-plan-name">Powersure Energy Control</div>
        <p className="text-[12px] text-white/65 mt-1 leading-snug" data-testid="account-price-line">
          € 9,95 per maand de eerste 3 maanden, daarna € 14,95 per maand. Maandelijks opzegbaar via Stripe.
        </p>
        <div className="mt-2 flex items-center gap-2 text-[12px]">
          <span className="text-white/55">Status:</span>
          <span
            className={
              sub.status === 'active'
                ? 'text-emerald-300'
                : sub.status === 'past_due'
                ? 'text-amber-300'
                : sub.status === 'cancelled'
                ? 'text-rose-300'
                : 'text-white/75'
            }
            data-testid="account-sub-status"
          >
            {statusLabel}
          </span>
        </div>
        <button
          onClick={openPortal}
          disabled={billingBusy || !portal.available}
          className={`mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors ${
            portal.available && !billingBusy
              ? 'bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold'
              : 'bg-white/5 text-white/55 cursor-not-allowed border border-white/10'
          }`}
          data-testid="account-billing-portal-btn"
        >
          {billingBusy ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Portal openen…</>
          ) : (
            'Beheer betaling en abonnement'
          )}
        </button>
        {!portal.available && (
          <p className="mt-2 text-[11.5px] text-white/55" data-testid="account-portal-hint">
            {portal.hint || 'Beheer wordt actief zodra uw eerste betaling door Stripe bevestigd is.'}
          </p>
        )}
        {billingErr && (
          <p className="mt-2 text-[12px] text-rose-300" data-testid="account-billing-error">{billingErr}</p>
        )}
      </Card>

      <Card>
        <div className="text-[10.5px] uppercase tracking-wider text-white/55 font-medium">Sessie</div>
        <button onClick={onLogout} className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 bg-white/5 text-white/85 text-sm" data-testid="account-logout-btn">
          <LogOut className="w-3.5 h-3.5" /> Uitloggen
        </button>
      </Card>

      {/* Fase 3.4 — Mijn device-data zelfservice (klant-gated, billing beschermd) */}
      <MyDeviceDataPanel sessionId={deviceDataSid} />

      {/* Fase 3.6 — Read-only pilot opt-in/out (consent v1-2026-02) */}
      <PilotOptInCard sessionId={deviceDataSid} />
    </div>
  );
}

function HelpScreen({ mailDryRun }) {
  return (
    <div className="space-y-3" data-testid="screen-help">
      <ScreenTitle badge="Hulp" title="Veelgestelde vragen" sub="Korte antwoorden over apparaten, P1, betaling en opzeggen." />
      <Card>
        <ul className="text-sm text-white/85 space-y-2">
          <li><strong>P1-meter:</strong> nodig voor live inzicht. Zonder werkt de app ook, met voorbeeld-data.</li>
          <li><strong>Apparaten:</strong> u kunt foto, QR of handmatig toevoegen. Sturing alleen met uw goedkeuring.</li>
          <li><strong>Betaling:</strong> € 9,95 eerste 3 maanden, daarna € 14,95. Maandelijks opzegbaar via Stripe.</li>
          <li><strong>Opzeggen:</strong> via de Stripe-link in uw bevestigingsmail.</li>
        </ul>
      </Card>
      <Card>
        <div className="text-[10.5px] uppercase tracking-wider text-white/55 font-medium">Contact</div>
        {mailDryRun ? (
          <p className="text-[12px] text-amber-300/90 mt-2 leading-snug" data-testid="help-mail-dryrun">
            Mail-support wordt nog geactiveerd. Bekijk voorlopig de veelgestelde vragen.
          </p>
        ) : (
          <p className="text-[12px] text-white/75 mt-2">Mail ons via <a href="/contact" className="text-emerald-300 underline">/contact</a>.</p>
        )}
      </Card>
    </div>
  );
}

/* ----------------- Shell ----------------- */
export default function CustomerAppShell({ user, onLogout }) {
  // Blok 85 — emit one server-side `app_opened` event on mount (idempotent
  // per page-load — React StrictMode in dev fires effects twice; that's
  // acceptable noise in analytics).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { trackFunnelEvent } = await import('@/utils/tracking');
        if (!cancelled) trackFunnelEvent('app_opened', { flow: 'customer_app' });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Blok 82 — switch <link rel="manifest"> naar de klant-app variant zodra de
  // klant in /app zit. Voorkomt dat Chrome's install-prompt het admin-manifest
  // ("Powersure Control Center") oppakt voor een klant. Reset bij unmount.
  useEffect(() => {
    let prevHref = null;
    try {
      const link = document.querySelector('link[rel="manifest"]');
      if (link) {
        prevHref = link.getAttribute('href');
        link.setAttribute('href', '/manifest-app.json');
      }
    } catch { /* ignore */ }
    return () => {
      try {
        const link = document.querySelector('link[rel="manifest"]');
        if (link && prevHref) link.setAttribute('href', prevHref);
      } catch { /* ignore */ }
    };
  }, []);
  // Blok 81 — when Stripe Customer Portal redirects back to /app?billing=returned
  // we want to land on the Account tab so the friendly "U bent terug" banner
  // is immediately visible. Other query params land on home as before.
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('billing') === 'returned') return 'account';
    } catch { /* ignore */ }
    return 'home';
  })();
  const [tab, setTab] = useState(initialTab);
  const [profile, setProfile] = useState(null);
  const [devices, setDevices] = useState([]);
  const [advice, setAdvice] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [account, setAccount] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const reloadProfile = useCallback(async () => {
    try { const r = await customerApi.get('/customer/app/profile'); setProfile(r.data); } catch { /* ignore */ }
  }, []);
  const reloadDevices = useCallback(async () => {
    try { const r = await customerApi.get('/customer/app/devices'); setDevices(r.data.items || []); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([
        reloadProfile(),
        reloadDevices(),
        customerApi.get('/customer/app/advice').then((r) => setAdvice(r.data.items || [])).catch(() => setAdvice([])),
        customerApi.get('/customer/app/quotes').then((r) => setQuotes(r.data.items || [])).catch(() => setQuotes([])),
        customerApi.get('/customer/app/account').then((r) => setAccount(r.data)).catch(() => setAccount(null)),
      ]);
    })();
  }, [reloadProfile, reloadDevices]);

  // Show onboarding modal once if not yet completed
  useEffect(() => {
    if (!profile) return;
    if (!profile.setup_completed && !isLocalSetupCompleted()) {
      const t = window.setTimeout(() => setShowSetup(true), 400);
      return () => window.clearTimeout(t);
    }
  }, [profile]);

  const onSetupCompleted = useCallback(async (payload) => {
    try {
      await customerApi.put('/customer/app/onboarding', {
        goal: payload.goal,
        has_p1: payload.has_p1,
        devices_intent: payload.devices,
        control_preference: payload.control_pref,
        completed: true,
      });
      reloadProfile();
    } catch { /* ignore */ }
  }, [reloadProfile]);

  const onAddDevice = useCallback(() => setAddOpen(true), []);
  const onAddedDevice = useCallback(() => { reloadDevices(); }, [reloadDevices]);
  const onDeleteDevice = useCallback(async (id) => {
    try { await customerApi.delete(`/customer/app/devices/${id}`); reloadDevices(); } catch { /* ignore */ }
  }, [reloadDevices]);
  const onP1Pick = useCallback(async (kind) => {
    try { await customerApi.post('/customer/app/p1/intent', { kind }); reloadProfile(); } catch { /* ignore */ }
  }, [reloadProfile]);

  const mailDryRun = !!(account?.support?.email_dry_run);

  return (
    <AppSidebar tab={tab} onChange={setTab}>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto pb-28 md:pb-6">
        {tab === 'home' && <OverviewScreen profile={profile} devices={devices} user={user} onAddDevice={onAddDevice} onP1={() => setTab('p1')} />}
        {tab === 'apparaten' && <DevicesScreen devices={devices} onAdd={onAddDevice} onDelete={onDeleteDevice} />}
        {tab === 'p1' && <P1Screen p1={profile?.onboarding ? (profile?.preferences?.p1 || profile?.p1) : null} onSet={onP1Pick} />}
        {tab === 'zonnestroom' && <SolarScreen />}
        {tab === 'verbruik' && <UsageScreen />}
        {tab === 'advies' && <AdviceScreen advice={advice} onAddDevice={onAddDevice} />}
        {tab === 'offertes' && <QuotesScreen quotes={quotes} />}
        {tab === 'account' && <AccountScreen account={account} onLogout={onLogout} />}
        {tab === 'help' && <HelpScreen mailDryRun={mailDryRun} />}
      </div>
      <AppBottomNav tab={tab} onChange={setTab} />
      <AddDeviceFlow open={addOpen} onClose={() => setAddOpen(false)} onAdded={onAddedDevice} />
      {showSetup && (
        <AppOnboardingSetup
          pilotName={user?.email || ''}
          onClose={() => setShowSetup(false)}
          onCompleted={(p) => { setShowSetup(false); onSetupCompleted(p); }}
        />
      )}
    </AppSidebar>
  );
}
